import { CSSProperties, HTMLProps } from 'react';
import styled, { css } from 'styled-components';
import { shouldIgnoreProp } from '../../utils/shouldIgnoreProp';
import { Flex, FlexProps } from '../layout/Flex';

type ButtonColor = 'primary' | 'invert' | 'secondary' | 'white' | 'hardLight';

export interface ButtonProps
    extends FlexProps,
        Omit<HTMLProps<HTMLButtonElement>, 'color' | 'size' | 'width' | 'height'> {
    size?: 's' | 'm' | 'l' | 'xl';
    color?: ButtonColor;
    variant?: 'filled' | 'outlined' | 'mirror';
}

type ButtonFilledSchema = {
    color: CSSProperties['color'];
    backgrounds: {
        default?: CSSProperties['backgroundColor'];
        hover: CSSProperties['backgroundColor'];
    };
};

const FILLED_BUTTON_SCHEMAS: Record<ButtonColor, ButtonFilledSchema> = {
    primary: {
        color: 'var(--control-primary-color)',
        backgrounds: {
            default: 'var(--control-primary-background)',
            hover: 'var(--control-primary-background-hover)',
        },
    },

    secondary: {
        color: 'var(--control-secondary-color)',
        backgrounds: {
            default: 'var(--control-secondary-background)',
            hover: 'var(--control-secondary-background-hover)',
        },
    },

    invert: {
        color: 'var(--control-invert-color)',
        backgrounds: {
            default: 'var(--control-invert-background)',
            hover: 'var(--control-invert-background-hover)',
        },
    },

    white: {
        color: '#000000',
        backgrounds: {
            default: '#FFFFFF',
            hover: '#F2F2F2',
        },
    },

    hardLight: {
        color: '#000000',
        backgrounds: {
            default: 'rgba(255, 255, 255, 0.65)',
            hover: 'rgba(255, 255, 255, 0.8)',
        },
    },
};

const makeButtonFilledStyles = (schema: ButtonFilledSchema) => css`
    color: ${schema.color};
    background-color: ${schema.backgrounds.default ?? 'transparent'};
    font-weight: 500;
    -webkit-backdrop-filter: blur(var(--blur-radius-l));
    backdrop-filter: blur(var(--blur-radius-l));

    &:not([disabled]):active,
    &:not([disabled]).active,
    &:not([disabled]):hover,
    &:not([disabled]):focus {
        background-color: ${schema.backgrounds.hover};
    }
`;

const OUTLINED_BUTTON_SCHEMAS: Record<ButtonColor, string> = {
    primary: 'var(--control-primary-background)',
    secondary: 'var(--control-secondary-background)',
    invert: 'var(--control-invert-background)',
    white: '#FFFFFF',
    hardLight: 'rgba(255, 255, 255, 0.65)',
};

const makeButtonOutlinedStyles = (color: string) => css`
    border: 1px solid ${color};
    color: ${color};
    background-color: transparent;
    font-weight: 500;

    &:not([disabled]):active,
    &:not([disabled]).active,
    &:not([disabled]):hover,
    &:not([disabled]):focus {
        opacity: 0.75;
    }
`;

export const Button = styled(Flex).withConfig({
    displayName: 'Button',
    shouldForwardProp: shouldIgnoreProp('width', 'size', 'color'),
})<ButtonProps>`
    box-sizing: border-box;
    display: inline-flex;
    -webkit-tap-highlight-color: transparent;
    background-color: transparent;
    outline: 0;
    border: 0;
    margin: 0;
    cursor: pointer;
    user-select: none;
    appearance: none;
    text-decoration: none;
    text-align: center;
    font-family: inherit;

    &[disabled] {
        color: var(--control-disabled-color);
        background-color: var(--control-disabled-background);
        cursor: not-allowed;
    }

    text-transform: uppercase;
    transition-property: color, background-color, border-color;
    transition-duration: 0.15s;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    font-weight: 400;

    ${(props) =>
        props.variant === 'filled' &&
        makeButtonFilledStyles(FILLED_BUTTON_SCHEMAS[props.color ?? 'primary'])}

    ${(props) =>
        props.variant === 'outlined' &&
        makeButtonOutlinedStyles(OUTLINED_BUTTON_SCHEMAS[props.color ?? 'primary'])}
    
    ${(props) =>
        props.size === 's' &&
        css`
            padding: 8px 20px;
            font-size: 10px;
            line-height: 16px;
        `}

    ${(props) =>
        props.size === 'm' &&
        css`
            font-size: 12px;
            line-height: 16px;
            padding: 8px 20px;
        `}

    ${(props) =>
        props.size === 'l' &&
        css`
            font-size: 13px;
            line-height: 16px;
            padding: 8px 20px;
        `}
    ${(props) =>
        props.textTransform &&
        css`
            text-transform: ${props.textTransform};
        `}
    
    ${(props) =>
        props.size === 'xl' &&
        css`
            font-size: 14px;
            line-height: 16px;
            padding: 14px 20px;
        `}
`;

Button.defaultProps = {
    as: 'button',
    position: 'relative',
    size: 'm',
    variant: 'filled',
    direction: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 1000,
};
